/**
 * Referencia Code128 para resumen de pago
 * Formato: 10 dígitos + guion + DDMMYY → ^\d{10,}-\d{6}$
 */

export const PAYMENT_BARCODE_REFERENCE_PATTERN = /^\d{10,}-\d{6}$/;

export const BARCODE_CONTEXT = {
  RECORDS: 'records',
  RESPONSE: 'response',
  APARTADO: 'apartado',
};

const DIGITS_ONLY = /\D/g;

const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;
const SLASH_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const COMPACT_DATE_8 = /^(\d{2})(\d{2})(\d{4})$/;
const COMPACT_DATE_6 = /^(\d{2})(\d{2})(\d{2})$/;
const DATE_SEGMENT_6 = /^\d{6}$/;

/**
 * @param {object} [source]
 * @returns {object|null}
 */
const normalizeSource = (source) => {
  if (!source || typeof source !== 'object') return null;
  const inner = source.data;
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const { data, ...outer } = source;
    return { ...inner, ...outer };
  }
  return source;
};

/**
 * @param {string|number} day
 * @param {string|number} month
 * @returns {boolean}
 */
const isValidDayMonth = (day, month) => {
  const d = Number(day);
  const m = Number(month);
  return m >= 1 && m <= 12 && d >= 1 && d <= 31;
};

/**
 * @param {string|number} value
 * @returns {string}
 */
const pad2 = (value) => String(value).padStart(2, '0');

/**
 * @param {unknown} value
 * @returns {boolean}
 */
const hasText = (value) => {
  if (value == null) return false;
  if (value instanceof Date) return !Number.isNaN(value.getTime());
  const str = typeof value === 'string' ? value.trim() : String(value).trim();
  return str !== '';
};

/**
 * @param {object} data
 * @param {string[]} keys
 * @returns {unknown}
 */
const pickFirst = (data, keys) => {
  if (!data || typeof data !== 'object') return null;
  for (let i = 0; i < keys.length; i += 1) {
    const value = data[keys[i]];
    if (hasText(value)) return value;
  }
  return null;
};

/**
 * Valida referencia Code128 (10 dígitos + guion + DDMMYY).
 * @param {unknown} value
 * @returns {boolean}
 */
export const isValidPaymentBarcodeReference = (value) => (
  typeof value === 'string' && PAYMENT_BARCODE_REFERENCE_PATTERN.test(value)
);

/**
 * Normaliza identificador de barcode (dígitos; pad a 10 si es más corto).
 * @param {unknown} value
 * @returns {string|null}
 */
export const normalizePaymentIdentifier = (value) => {
  if (value == null || value === '') return null;
  const str = typeof value === 'string' ? value.trim() : String(value).trim();
  if (!str) return null;
  const digits = str.replace(DIGITS_ONLY, '');
  if (!digits || digits.length === 0) return null;
  if (digits.length >= 10) return digits;
  return digits.padStart(10, '0');
};

/**
 * Convierte fecha a DDMMYY.
 * @param {unknown} value
 * @returns {string|null}
 */
export const normalizePaymentDate = (value) => {
  if (value == null || value === '') return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const day = pad2(value.getDate());
    const month = pad2(value.getMonth() + 1);
    const year = String(value.getFullYear()).slice(-2);
    if (!isValidDayMonth(day, month)) return null;
    return `${day}${month}${year}`;
  }

  const str = typeof value === 'string' ? value.trim() : String(value).trim();
  if (!str) return null;

  const isoMatch = str.match(ISO_DATE_PREFIX);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    if (!isValidDayMonth(day, month)) return null;
    return `${day}${month}${year.slice(-2)}`;
  }

  const slashMatch = str.match(SLASH_DATE);
  if (slashMatch) {
    const [, dayRaw, monthRaw, year] = slashMatch;
    const day = pad2(dayRaw);
    const month = pad2(monthRaw);
    if (!isValidDayMonth(day, month)) return null;
    return `${day}${month}${year.slice(-2)}`;
  }

  const digitsOnly = str.replace(DIGITS_ONLY, '');
  if (digitsOnly.length === 8 && COMPACT_DATE_8.test(digitsOnly)) {
    const [, day, month, year] = digitsOnly.match(COMPACT_DATE_8);
    if (!isValidDayMonth(day, month)) return null;
    return `${day}${month}${year.slice(-2)}`;
  }
  if (digitsOnly.length === 6 && COMPACT_DATE_6.test(digitsOnly)) {
    const [, day, month] = digitsOnly.match(COMPACT_DATE_6);
    if (!isValidDayMonth(day, month)) return null;
    return digitsOnly;
  }

  return null;
};

/**
 * @param {unknown} value
 * @returns {string|null}
 */
export const normalizeBarcodeReference = (value) => {
  if (!hasText(value)) return null;
  const asString = typeof value === 'string' ? value.trim() : String(value).trim();
  if (isValidPaymentBarcodeReference(asString)) return asString;

  const separatorIndex = asString.indexOf('-');
  if (separatorIndex < 0) return null;

  const left = asString.slice(0, separatorIndex);
  const right = asString.slice(separatorIndex + 1);
  const normalizedId = normalizePaymentIdentifier(left);
  if (!normalizedId) return null;

  let dateSegment = null;
  if (DATE_SEGMENT_6.test(right)) {
    const day = right.slice(0, 2);
    const month = right.slice(2, 4);
    if (!isValidDayMonth(day, month)) return null;
    dateSegment = right;
  } else {
    dateSegment = normalizePaymentDate(right);
  }
  if (!dateSegment) return null;

  const reference = `${normalizedId}-${dateSegment}`;
  return isValidPaymentBarcodeReference(reference) ? reference : null;
};

/**
 * @param {object} data
 * @returns {unknown}
 */
export const resolveEmpaquesDocumentNumber = (data) => {
  const empaques = Array.isArray(data?.empaques) ? data.empaques : [];
  if (!empaques.length) return null;

  const documentNumbers = empaques
    .map((item) => item?.documentNumber)
    .filter((value) => hasText(value))
    .map((value) => String(value).trim());

  if (!documentNumbers.length) return null;

  const [first] = documentNumbers;
  const allSame = documentNumbers.every((value) => value === first);
  return allSame ? first : null;
};

/**
 * @param {object} paymentData
 * @param {object} [section]
 * @param {unknown} [routeId]
 * @returns {unknown}
 */
const resolveApartadoIdentifier = (paymentData, section, routeId) => (
  pickFirst(paymentData, [
    'notaVenta',
    'NotaVenta',
    'idNotaVenta',
    'idApartado',
  ])
    ?? pickFirst(section, [
      'notaVenta',
      'NotaVenta',
      'idNotaVenta',
      'idApartado',
      'id',
    ])
    ?? routeId
);

/**
 * @param {unknown} identifier
 * @param {unknown} paymentDate
 * @returns {string|null}
 */
export const buildPaymentBarcodeReference = (identifier, paymentDate) => {
  const normalizedCode = normalizePaymentIdentifier(identifier);
  const dateSegment = normalizePaymentDate(paymentDate);
  if (!normalizedCode || !dateSegment) return null;

  const reference = `${normalizedCode}-${dateSegment}`;
  return isValidPaymentBarcodeReference(reference) ? reference : null;
};

/**
 * @param {string|null} derived
 * @param {object} paymentData
 * @returns {string|null}
 */
const preferDerivedOrApiBarcode = (derived, paymentData) => {
  const apiReference = normalizeBarcodeReference(paymentData?.barcode_reference);
  if (derived && apiReference && derived !== apiReference) {
    return derived;
  }
  if (derived) return derived;
  if (apiReference) return apiReference;
  return null;
};

/**
 * Records: paymentSapNumber + createdAt
 * @param {object} paymentData
 * @returns {string|null}
 */
const resolveRecordsBarcode = (paymentData) => {
  const identifier = paymentData?.paymentSapNumber;
  const dateValue = paymentData?.createdAt;
  return buildPaymentBarcodeReference(identifier, dateValue);
};

/**
 * @param {object} paymentData
 * @param {unknown} date
 * @returns {string|null}
 */
const resolveResponseBarcode = (paymentData, date) => {
  const identifier = resolveEmpaquesDocumentNumber(paymentData);
  return buildPaymentBarcodeReference(identifier, date);
};

/**
 * @param {object} paymentData
 * @param {object} [section]
 * @param {unknown} [id]
 * @param {unknown} [date]
 * @returns {string|null}
 */
const resolveApartadoBarcode = (paymentData, section, id, date) => {
  const identifier = resolveApartadoIdentifier(paymentData, section, id);
  const dateValue = date
    ?? paymentData?.createdAt
    ?? null;
  return buildPaymentBarcodeReference(identifier, dateValue);
};

/**
 * @param {object} [source]
 * @returns {string|null}
 */
export const resolvePaymentBarcodeReference = (source) => {
  const data = normalizeSource(source);
  if (!data || typeof data !== 'object') return null;

  const derived = resolveRecordsBarcode(data)
    ?? resolveApartadoBarcode(data, null, null, null);

  return preferDerivedOrApiBarcode(derived, data);
};

/**
 * Resuelve el barcode Montepío según contexto explícito.
 *
 * @param {object} [options]
 * @param {object} [options.payment]
 * @param {string} [options.type]
 * @param {string|number} [options.id]
 * @param {object} [options.section]
 * @param {unknown} [options.date] fecha visible (requerida en response)
 * @param {string} [options.context] records | response | apartado
 * @returns {string|null}
 */
export const resolvePaymentSummaryBarcodeReference = ({
  payment = null,
  id = null,
  section = null,
  date = null,
  context = BARCODE_CONTEXT.RECORDS,
} = {}) => {
  const paymentData = normalizeSource(payment) || {};
  const ctx = String(context || '').trim().toLowerCase();

  let derived = null;
  if (ctx === BARCODE_CONTEXT.RESPONSE) {
    derived = resolveResponseBarcode(paymentData, date);
  } else if (ctx === BARCODE_CONTEXT.APARTADO) {
    derived = resolveApartadoBarcode(paymentData, section, id, date);
  } else {
    derived = resolveRecordsBarcode(paymentData);
  }

  return preferDerivedOrApiBarcode(derived, paymentData);
};
